import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { FirebaseService } from '../../services/firebase/firebase.service';
import * as fromReviewsActions from '../actions/reviews.actions';
import { Action } from '@ngrx/store';
import { Observable, switchMap, take, map, catchError, of, from, forkJoin } from 'rxjs';
import { User } from '../../models/user.model';
import { MultimediaObject, Review, ReviewFirebaseResponse, ReviewFormFirebaseResponse } from '../../models/reviews.model';
import { Firestore, collection, doc, query, where } from '@angular/fire/firestore';
import { getDoc } from 'firebase/firestore';

@Injectable()
export class ReviewsEffects {

    constructor(
        private readonly action$: Actions,
        private readonly firestore: Firestore,
        private readonly firebaseService: FirebaseService,
    ) { }

    // Load Reviews
    loadReviews: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromReviewsActions.LoadReviewsAction),
        switchMap(({ data }: { data: User }) => this.getReviewsForTrainer(data).pipe(
            switchMap((reviews) => this.addUsersToReviews(reviews, data)),
            map((reviews) => ({
              type: fromReviewsActions.LOAD_REVIEWS_SUCCESS,
              data: reviews
            })),
            catchError((error) =>
              of({ type: fromReviewsActions.LOAD_REVIEWS_FAIL, error })
            )
          )
        )));

      // Send Review
      sendReview$: Observable<Action> = createEffect(() => this.action$.pipe(
        ofType(fromReviewsActions.SendReviewsAction),
        switchMap(async ({ review, multimediaObjects }: { review: Review; multimediaObjects: MultimediaObject[] }) => {        
            const multimediaToRemove = review.multimediaContent
              .filter((name) => !multimediaObjects.some((multimediaObject) => !multimediaObject.isNew && name === multimediaObject.name));
            const multimediaToAdd = multimediaObjects.filter((multimediaObject) => multimediaObject.isNew);

            const reviewToReturn = { ...review };
            for await (const multimedia of multimediaToAdd) {
              await this.firebaseService.uploadFiles(multimedia.src, `reviews/${review.user?.id}/${review.id}`, multimedia.name);
              reviewToReturn.multimediaContent = [ ...reviewToReturn.multimediaContent, ...[multimedia.name] ];
            }
            for await (const multimedia of multimediaToRemove) {
              await this.firebaseService.deleteFile(`reviews/${review.user?.id}/${review.id}/${multimedia}`);
              reviewToReturn.multimediaContent = review.multimediaContent.filter((multimediaCont) => multimediaCont !== multimedia )
            }

            const reviewToUpdate = { ...reviewToReturn };
            delete reviewToUpdate.user;
            delete reviewToUpdate.id;
            delete reviewToUpdate.form;
            await this.firebaseService.updateDoc('reviews', review.id!, reviewToUpdate);
            
            return reviewToReturn;
        }),
        map((review) => ({ type: fromReviewsActions.SEND_REVIEWS_SUCCESS, review })),
        catchError(error => of({ type: fromReviewsActions.SEND_REVIEWS_FAIL, error: error }))
    ));

    private getReviewsForTrainer(
        user: User,
      ): Observable<ReviewFirebaseResponse[]> {
        const userRef = doc(this.firestore, `users/${user.id}`);

        const ref = query(
            collection(this.firestore, 'reviews'),
            where('userRef', '==', userRef)
          );

        return this.firebaseService.getDataOfCollectionByField(ref).pipe(take(1), map((resp) => resp.map((doc) => ({
            ...doc as ReviewFirebaseResponse
          }))));
      }
    
      private addUsersToReviews(
        reviews: ReviewFirebaseResponse[],
        user: User
      ): Observable<Review[]> {
        let reviewsMapped: Review[] = [];
        if (reviews?.length > 0) {
          const formsAlreadyAdded: string[] = [];
          const formsObservables: Observable<{ id: string; [key: string]: any }>[] = [];
          reviews.forEach((review) => {
            if (!formsAlreadyAdded.includes(review.formRef!.id)) {
              formsAlreadyAdded.push(review.formRef!.id);
              formsObservables.push(from(getDoc(review.formRef!)).pipe(map((document) => ({ ...document.data(), id: document.id }))));
            }
          });
    
          const formResults$ = forkJoin(formsObservables);
          return formResults$.pipe(
              map(
                (forms) =>
                  (reviewsMapped = reviews.map((review) => {  
                    const form: ReviewFormFirebaseResponse = {
                      ...forms
                        .find((formDoc: any) => formDoc.id === review.formRef!.id),
                      id: review.formRef!.id
                    } as any;
      
                    delete review.userRef;
                    delete review.trainerRef;
                    delete review.formRef;
      
                    delete user.trainerRef;
                    delete user.teamRef;
      
                    delete form.trainerRef;
      
                    return {
                      ...review,
                      reviewDate: new Date(review.reviewDate.seconds * 1000),
                      reviewedDate: review.reviewedDate
                        ? new Date(review.reviewDate.seconds * 1000)
                        : null,
                      updatedDate: review.updatedDate
                        ? new Date(review.updatedDate.seconds * 1000)
                        : null,
                      user,
                      form
                    } as Review;
                  }))
              )
            );
          }
        return of(reviewsMapped);
      }
}
